import { useCallback, useEffect, useState } from "react";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import PageLayout from "components/page-layout";
import Table, { tableHeader } from "components/tables/table";
import { oneLine, mutedLine } from "components/tables/table-parts";
import ActionButton from "components/tables/action-btn";
import NewTabSection from "components/modals/new-tab-section";
import DangerModal from "components/modals/danger";
import { getTabs, deleteTab } from "api/tabs";
import { getMiniApps } from "api/mini-apps";
import {
  Tab,
  TAB_STATUSES,
  TAB_TYPE_SUGGESTIONS,
  TabsResponseType,
  emptyTabsResponse,
} from "types/tab-types";
import { MiniAppType } from "types/mini-app-types";
import { FiltersList } from "types/page-layout-types";
import { useSharedState } from "sharedStateProvider";
import { getApiErrorMessage } from "api/error";

const labelOf = (
  options: { value: string; label: string }[],
  value: string
): string => options.find((o) => o.value === value)?.label ?? value;

const TabsFiltersList: FiltersList = {
  type: [
    { name: "Все типы", value: "", filter_name: "type", disabled: false },
    ...TAB_TYPE_SUGGESTIONS.map((t) => ({
      name: t.label,
      value: t.value,
      filter_name: "type",
      disabled: false,
    })),
  ],
  status: [
    { name: "Все статусы", value: "", filter_name: "status", disabled: false },
    ...TAB_STATUSES.map((s) => ({
      name: s.label,
      value: s.value,
      filter_name: "status",
      disabled: false,
    })),
  ],
};

export default function TabsSection() {
  const { notify, isSuperAdmin } = useSharedState();

  const [tabsResp, setTabsResp] = useState<TabsResponseType>(emptyTabsResponse);
  const [miniAppsMap, setMiniAppsMap] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [firstEntry, setFirstEntry] = useState<number>(0);

  const [searchParams] = useState<URLSearchParams>(new URLSearchParams());

  const [editingTab, setEditingTab] = useState<Tab | null>(null);
  const [deletingTab, setDeletingTab] = useState<Tab | null>(null);

  const updateFirstEntry = (value: number) => setFirstEntry(value);

  const loadMiniApps = useCallback(async () => {
    try {
      const res = await getMiniApps(new URLSearchParams());
      const map: Record<string, string> = {};
      (res.data ?? []).forEach((app: MiniAppType) => {
        map[app.id] = app.names.ru || app.names.en;
      });
      setMiniAppsMap(map);
    } catch (error: unknown) {
      notify(
        getApiErrorMessage(error, "Не удалось загрузить мини-приложения"),
        "danger",
        "Ошибка загрузки мини-приложений"
      );
    }
  }, [notify]);

  const loadTabs = useCallback(
    async (page?: number) => {
      setIsLoading(true);
      try {
        const res: TabsResponseType = await getTabs(searchParams, page);
        setTabsResp(res);
      } catch (error: unknown) {
        notify(
          getApiErrorMessage(error, "Не удалось загрузить вкладки"),
          "danger",
          "Ошибка загрузки вкладок"
        );
      } finally {
        setIsLoading(false);
      }
    },
    [notify, searchParams]
  );

  const appendParam = (key: string, value: string) => {
    if (value) {
      if (searchParams.has(key)) searchParams.set(key, value);
      else searchParams.append(key, value);
    } else {
      searchParams.delete(key);
    }
  };

  const clearFilters = async (param?: string) => {
    if (param) {
      searchParams.delete(param);
    } else {
      searchParams.delete("type");
      searchParams.delete("status");
      searchParams.delete("search");
    }
  };

  useEffect(() => {
    loadTabs();
    loadMiniApps();
  }, [loadTabs, loadMiniApps]);

  const onEdit = (tab: Tab) => setEditingTab(tab);
  const onDangerModal = (tab: Tab) => setDeletingTab(tab);

  const confirmDelete = async () => {
    if (!deletingTab) return;
    try {
      await deleteTab(deletingTab.id);
      notify("Таб успешно удалён", "success");
      loadTabs();
    } catch (error: unknown) {
      notify(
        getApiErrorMessage(error, "Не удалось удалить таб"),
        "danger",
        "Ошибка удаления таба"
      );
    } finally {
      setDeletingTab(null);
    }
  };

  const tabsTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Название", narrow: false, wrap: false },
    { header_text: "Тип", narrow: true, wrap: false },
    { header_text: "Статус", narrow: true, wrap: false },
    { header_text: "Мини-приложение", narrow: false, wrap: false },
    { header_text: "", narrow: true, wrap: false },
  ];

  const tabsList = (
    <>
      {tabsResp.data.map((tab, index) => (
        <tr key={tab.id}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>{oneLine(tab.title || "—")}</td>
          <td>{mutedLine(labelOf(TAB_TYPE_SUGGESTIONS, tab.type))}</td>
          <td>
            <span
              className={`badge ${
                tab.status === "production" ? "bg-green-lt" : "bg-yellow-lt"
              }`}
            >
              {labelOf(TAB_STATUSES, tab.status)}
            </span>
          </td>
          <td>
            {oneLine(
              tab.mini_app_id
                ? miniAppsMap[tab.mini_app_id] || tab.mini_app_id
                : "—"
            )}
          </td>
          <td>
            {isSuperAdmin && (
              <div className="btn-list flex-nowrap">
                <ActionButton
                  icon={<IconPencil className="icon" />}
                  tooltip="Изменить"
                  action={() => onEdit(tab)}
                  color={"info"}
                  modal={true}
                  modal_id="#modal-new-tab-section"
                />
                <ActionButton
                  icon={<IconTrash className="icon" />}
                  tooltip="Удалить"
                  action={() => onDangerModal(tab)}
                  color={"danger"}
                  modal={true}
                  modal_id="#modal-danger"
                />
              </div>
            )}
          </td>
        </tr>
      ))}
    </>
  );

  const tabsTable = Table(
    tabsTableHeaders,
    tabsList,
    true,
    tabsResp,
    updateFirstEntry,
    loadTabs
  );

  return (
    <>
      <PageLayout
        children={tabsTable}
        isLoading={isLoading}
        filters={true}
        filtersList={TabsFiltersList}
        appendFilter={appendParam}
        clearFilters={clearFilters}
        search={true}
        searchAPI={() => loadTabs(1)}
        dateRange={false}
        header="Табы приложения"
        subheaderText="Количество табов: "
        subheaderCount={tabsResp.total}
        dataLength={tabsResp.data.length}
        buttonText={isSuperAdmin ? "Создать таб" : undefined}
        buttonModal={isSuperAdmin ? "#modal-new-tab-section" : undefined}
      />
      <NewTabSection
        editingTab={editingTab}
        clearEditing={() => setEditingTab(null)}
        onSaved={loadTabs}
      />
      <DangerModal
        dangerDescription={
          deletingTab
            ? `Вы действительно хотите удалить таб «${deletingTab.title}»?`
            : ""
        }
        confirm={confirmDelete}
      />
    </>
  );
}
